import { Routes } from '@angular/router';
import { Observable, of as observableOf } from 'rxjs';

import { ClaimHelpComponent } from '@amfam/claim/detail/feature';
import { PostFnolRoutes } from '@amfam/claim/post-fnol/feature';
import { ReportClaimRiskSelectionComponent } from '@amfam/claim/shared/risk-selection';
import { PrettyClaimNum } from '@amfam/claim/shared/utility';

import { FeatureFlagGuard } from '../core';
import { ClaimResolve } from './claim-routing-resolver.service';
import { ContactAdjusterComponent } from './contact-adjuster';
import { PostFnolWrapperComponent } from './post-fnol/post-fnol-wrapper.component';
import { ReportClaimCallUsComponent } from './report-claim-fnol/report-claim-call-us';
import { ReportClaimContactInfoComponent } from './report-claim-fnol/report-claim-contact-info';
import { ReportClaimDuplicateComponent } from './report-claim-fnol/report-claim-duplicate';
import { ReportClaimFnolComponent } from './report-claim-fnol/report-claim-fnol.component';
import { ReportClaimIncidentLocationComponent } from './report-claim-fnol/report-claim-incident-location';
import { ReportClaimIncidentTimeComponent } from './report-claim-fnol/report-claim-incident-time';
import { ReportClaimLossCauseComponent } from './report-claim-fnol/report-claim-loss-cause';
import { ReportClaimWhatToExpectComponent } from './report-claim-fnol/report-claim-what-to-expect/report-claim-what-to-expect.component';
import { ReportClaimGlassOnlyComponent } from './report-claim-glass-only';
// Guards
import { ClaimCustomerGuard } from './shared/guard/claim.guard';
import { FnolRiskLookInGuard } from './shared/guard/fnol.guard';
import { PostFnolGuard } from './shared/guard/post-fnol.guard';

export function param(params: any): Observable<string> {
  // transform param to show formatted cliam number
  let claimNumber = params.id;
  if (params.id && params.id.length) {
    if (params.id.length === 11) {
      claimNumber = 'Claim ' + new PrettyClaimNum().transform(params.id);
    }
  }

  return observableOf(claimNumber);
}

export const ClaimRoutes: Routes = [
  {
    path: 'claims',
    data: {
      breadcrumb: 'Claims'
    },
    canActivate: [ClaimCustomerGuard],
    children: [
      { path: 'error', component: ClaimHelpComponent, data: { breadcrumb: 'Error' } },
      {
        path: 'contact-adjuster/:id',
        component: ContactAdjusterComponent,
        data: { breadcrumb: 'Contact Adjuster' }
      },
      {
        path: 'dynamic-fnol',
        data: {
          breadcrumb: 'File a Claim'
        },
        loadChildren: () =>
          import('@amfam/claim/dynamic-fnol/feature').then(m => m.ClaimDynamicFnolFeatureModule)
      },
      {
        path: 'dynamic-fnol-overview',
        data: { breadcrumb: false },
        loadChildren: () =>
          import('@amfam/claim/dynamic-fnol/feature').then(m => m.ClaimDynamicFnolFeatureModule)
      },
      {
        path: 'dynamic-fnol-detail',
        data: { breadcrumb: 'Claim Details' },
        loadChildren: () =>
          import('@amfam/claim/dynamic-fnol/feature').then(m => m.ClaimDynamicFnolFeatureModule)
      },
      {
        path: 'what-to-expect',
        component: ReportClaimWhatToExpectComponent,
        data: { breadcrumb: 'What to expect' }
      },
      {
        path: 'report-claim-fnol',
        component: ReportClaimFnolComponent,
        canActivate: [FeatureFlagGuard],
        data: {
          feature: 'fnol',
          redirectRoute: 'claims/report-claim',
          breadcrumb: 'File a Claim'
        },
        children: [
          { path: '', component: ReportClaimRiskSelectionComponent, data: { breadcrumb: false } },
          {
            path: 'incident-time',
            component: ReportClaimIncidentTimeComponent,
            canActivate: [FnolRiskLookInGuard],
            resolve: {
              resolvedData: ClaimResolve
            },
            data: {
              breadcrumb: 'useResolvedData'
            }
          },
          {
            path: 'existing-claim/:id',
            component: ReportClaimDuplicateComponent,
            canActivate: [FnolRiskLookInGuard],
            resolve: {
              resolvedData: ClaimResolve
            },
            data: {
              breadcrumb: 'useResolvedData'
            }
          },
          {
            path: 'incident-location',
            component: ReportClaimIncidentLocationComponent,
            canActivate: [FnolRiskLookInGuard],
            resolve: {
              resolvedData: ClaimResolve
            },
            data: {
              breadcrumb: 'useResolvedData'
            }
          },
          {
            path: 'cause',
            component: ReportClaimLossCauseComponent,
            canActivate: [FnolRiskLookInGuard],
            resolve: {
              resolvedData: ClaimResolve
            },
            data: {
              breadcrumb: 'useResolvedData'
            }
          },
          {
            path: 'contact',
            component: ReportClaimContactInfoComponent,
            canActivate: [FnolRiskLookInGuard],
            resolve: {
              resolvedData: ClaimResolve
            },
            data: {
              breadcrumb: 'useResolvedData'
            }
          },
          {
            path: 'call-us/:reason',
            component: ReportClaimCallUsComponent,
            data: {
              breadcrumb: false
            }
          },
          {
            path: 'call-us',
            component: ReportClaimCallUsComponent,
            data: {
              breadcrumb: false
            }
          },
          {
            path: ':draftClaimNumber',
            component: ReportClaimFnolComponent,
            data: {
              breadcrumb: false
            }
          }
        ]
      },
      {
        path: 'glass-only',
        component: ReportClaimGlassOnlyComponent,
        canActivate: [FeatureFlagGuard],
        data: {
          feature: 'fnol',
          breadcrumb: 'Glass Only'
        }
      },
      {
        path: ':id/post-fnol',
        component: PostFnolWrapperComponent,
        data: {
          feature: 'post_fnol_flow',
          breadcrumb: 'Self Service'
        },
        canActivate: [FeatureFlagGuard, PostFnolGuard],
        children: PostFnolRoutes
      }
    ]
  },
  {
    path: 'overview',
    data: {
      breadcrumb: 'Error'
    },
    children: [{ path: 'error', component: ClaimHelpComponent, data: { breadcrumb: false } }]
  }
];

export const ClaimGuards = [ClaimCustomerGuard, FnolRiskLookInGuard, PostFnolGuard];
